import * as React2 from 'react';
import React2__default, { createContext, forwardRef, useRef, useState, useEffect, useCallback, useImperativeHandle, useMemo, useContext } from 'react';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { AnimatePresence, motion } from 'framer-motion';
import { X, MessageSquare, Loader2, Send } from 'lucide-react';
import { getRatingSlug, getRating as getRating$1 } from '@aiready/core/client';
import * as d32 from 'd3';

// src/components/button.tsx
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Platform specific high-polish variants
        glow: "bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 hover:scale-[1.02] active:scale-[0.98]",
        glass: "bg-slate-800/50 backdrop-blur-sm border border-slate-700 text-slate-200 hover:bg-slate-700/50 hover:text-white",
        accent: "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20",
        purple: "bg-purple-600 text-white shadow-lg shadow-purple-500/20 hover:bg-purple-500 hover:scale-[1.02] active:scale-[0.98]",
        amber: "bg-amber-500 text-slate-950 shadow-lg shadow-amber-500/10 hover:bg-amber-400 hover:scale-[1.02] active:scale-[0.98]",
        blue: "bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 hover:scale-[1.02] active:scale-[0.98]"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // Extra sizes for dashboard use
        xs: "h-7 rounded-md px-2 text-[10px] font-bold uppercase tracking-wider"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = React2.forwardRef(
  ({ className, variant, size, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "button",
      {
        className: cn(buttonVariants({ variant, size, className })),
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";
var Card = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn(
      "rounded-lg border bg-card text-card-foreground shadow-sm",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("flex flex-col space-y-1.5 p-6", className),
    ...props
  }
));
CardHeader.displayName = "CardHeader";
var CardTitle = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "h3",
  {
    ref,
    className: cn(
      "text-2xl font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "p",
  {
    ref,
    className: cn("text-sm text-muted-foreground", className),
    ...props
  }
));
CardDescription.displayName = "CardDescription";
var CardContent = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn("flex items-center p-6 pt-0", className),
    ...props
  }
));
CardFooter.displayName = "CardFooter";
var GlassCard = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn(
      "rounded-2xl border border-indigo-500/20 bg-slate-900/40 backdrop-blur-md shadow-xl transition-all hover:border-indigo-500/30",
      className
    ),
    ...props
  }
));
GlassCard.displayName = "GlassCard";
var GlassCardHeader = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn(
      "flex flex-col space-y-1.5 p-6 border-b border-indigo-500/10",
      className
    ),
    ...props
  }
));
GlassCardHeader.displayName = "GlassCardHeader";
var GlassCardContent = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-6", className), ...props }));
GlassCardContent.displayName = "GlassCardContent";
var Input = React2.forwardRef(
  ({ className, type, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "input",
      {
        type,
        className: cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Input.displayName = "Input";
var labelVariants = cva(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
);
var Label = React2.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("label", { ref, className: cn(labelVariants(), className), ...props })
);
Label.displayName = "Label";
var badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn(badgeVariants({ variant }), className), ...props });
}
function Modal({
  isOpen,
  onClose,
  children,
  maxWidth = "max-w-4xl",
  showClose = true,
  variant = "glass"
}) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);
  return /* @__PURE__ */ jsx(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsxs("div", { className: "fixed inset-0 z-[100] flex items-center justify-center p-4", children: [
    /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
        onClick: onClose,
        className: "absolute inset-0 bg-slate-950/60 backdrop-blur-md"
      }
    ),
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, scale: 0.95, y: 20 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.95, y: 20 },
        className: cn(
          "relative w-full max-h-[90vh] overflow-y-auto shadow-2xl transition-all",
          maxWidth,
          variant === "glass" ? "bg-slate-900/90 backdrop-blur-xl rounded-3xl border border-indigo-500/30" : "bg-white rounded-3xl",
          "scrollbar-thin scrollbar-thumb-slate-700"
        ),
        children: [
          showClose && /* @__PURE__ */ jsx(
            "button",
            {
              onClick: onClose,
              className: cn(
                "absolute top-6 right-6 p-2 rounded-full transition-colors z-50",
                variant === "glass" ? "text-slate-400 hover:text-white hover:bg-slate-800" : "bg-white/80 backdrop-blur-sm text-slate-500 hover:bg-slate-100"
              ),
              "aria-label": "Close modal",
              children: /* @__PURE__ */ jsx(X, { className: "w-6 h-6" })
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "p-2 sm:p-4", children })
        ]
      }
    )
  ] }) });
}
var STROKE_ICON_PROPS = {
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round"
};
function createStrokeIcon(_name, children, additionalProps) {
  return /* @__PURE__ */ jsx(IconBase, { ...STROKE_ICON_PROPS, ...additionalProps, children });
}
var IconBase = ({
  className = "w-6 h-6",
  children,
  viewBox = "0 0 24 24",
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      viewBox,
      fill: "none",
      stroke: "currentColor",
      className,
      ...props,
      xmlns: "http://www.w3.org/2000/svg",
      children
    }
  );
};
function AlertCircleIcon(props) {
  return createStrokeIcon(
    "AlertCircle",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
      /* @__PURE__ */ jsx("line", { x1: "12", y1: "8", x2: "12", y2: "12" }),
      /* @__PURE__ */ jsx("line", { x1: "12", y1: "16", x2: "12.01", y2: "16" })
    ] }),
    props
  );
}
function AlertTriangleIcon(props) {
  return createStrokeIcon(
    "AlertTriangle",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("path", { d: "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" }),
      /* @__PURE__ */ jsx("line", { x1: "12", y1: "9", x2: "12", y2: "13" }),
      /* @__PURE__ */ jsx("line", { x1: "12", y1: "17", x2: "12.01", y2: "17" })
    ] }),
    props
  );
}
function ArrowRightIcon(props) {
  return createStrokeIcon(
    "ArrowRight",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("line", { x1: "5", y1: "12", x2: "19", y2: "12" }),
      /* @__PURE__ */ jsx("polyline", { points: "12 5 19 12 12 19" })
    ] }),
    props
  );
}
function BrainIcon(props) {
  return createStrokeIcon(
    "Brain",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("path", { d: "M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.74-3.41A2.5 2.5 0 0 1 2 14c0-1.5 1-2 1-2s-1-.5-1-2a2.5 2.5 0 0 1 2.3-2.48A2.5 2.5 0 0 1 7 5.5a2.5 2.5 0 0 1 2.5-3.5z" }),
      /* @__PURE__ */ jsx("path", { d: "M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.74-3.41A2.5 2.5 0 0 0 22 14c0-1.5-1-2-1-2s1-.5 1-2a2.5 2.5 0 0 0-2.3-2.48A2.5 2.5 0 0 0 17 5.5a2.5 2.5 0 0 0-2.5-3.5z" })
    ] }),
    props
  );
}
function ChartIcon(props) {
  return /* @__PURE__ */ jsxs(IconBase, { ...props, children: [
    /* @__PURE__ */ jsx("rect", { x: "3", y: "3", width: "18", height: "18", rx: "2", strokeWidth: "1.2" }),
    /* @__PURE__ */ jsx("rect", { x: "7", y: "11", width: "2", height: "6", fill: "currentColor" }),
    /* @__PURE__ */ jsx("rect", { x: "11", y: "8", width: "2", height: "9", fill: "currentColor" }),
    /* @__PURE__ */ jsx("rect", { x: "15", y: "5", width: "2", height: "12", fill: "currentColor" })
  ] });
}
function ClockIcon(props) {
  return createStrokeIcon(
    "Clock",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
      /* @__PURE__ */ jsx("polyline", { points: "12 6 12 12 16 14" })
    ] }),
    props
  );
}
function FileIcon(props) {
  return createStrokeIcon(
    "File",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("path", { d: "M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" }),
      /* @__PURE__ */ jsx("polyline", { points: "14 2 14 8 20 8" })
    ] }),
    props
  );
}
function HammerIcon(props) {
  return createStrokeIcon(
    "Hammer",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("path", { d: "M18.42 13.59L7.46 2.63a1 1 0 0 0-1.42 0l-4.7 4.7a1 1 0 0 0 0 1.42L11 18.23l1.07-1.07-1.41-1.41 1.42-1.42 1.41 1.41 1.41-1.41-1.41-1.41 1.42-1.42 1.41 1.41 2-2z" }),
      /* @__PURE__ */ jsx("path", { d: "M13 18l6 6" })
    ] }),
    props
  );
}
function InfoIcon(props) {
  return createStrokeIcon(
    "Info",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
      /* @__PURE__ */ jsx("line", { x1: "12", y1: "16", x2: "12", y2: "12" }),
      /* @__PURE__ */ jsx("line", { x1: "12", y1: "8", x2: "12.01", y2: "8" })
    ] }),
    props
  );
}
function PlayIcon(props) {
  return createStrokeIcon(
    "Play",
    /* @__PURE__ */ jsx("polygon", { points: "5 3 19 12 5 21 5 3" }),
    props
  );
}
function RefreshCwIcon(props) {
  return createStrokeIcon(
    "RefreshCw",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("path", { d: "M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" }),
      /* @__PURE__ */ jsx("path", { d: "M21 3v5h-5" }),
      /* @__PURE__ */ jsx("path", { d: "M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" }),
      /* @__PURE__ */ jsx("path", { d: "M3 21v-5h5" })
    ] }),
    props
  );
}
function RobotIcon(props) {
  return /* @__PURE__ */ jsxs(IconBase, { ...props, children: [
    /* @__PURE__ */ jsx("rect", { x: "3", y: "7", width: "18", height: "11", rx: "2", strokeWidth: "1.2" }),
    /* @__PURE__ */ jsx("rect", { x: "7", y: "10", width: "2", height: "2", fill: "currentColor" }),
    /* @__PURE__ */ jsx("rect", { x: "15", y: "10", width: "2", height: "2", fill: "currentColor" }),
    /* @__PURE__ */ jsx("path", { d: "M9 3v2M15 3v2", strokeWidth: "1.2", strokeLinecap: "round" })
  ] });
}
function RocketIcon(props) {
  return /* @__PURE__ */ jsxs(IconBase, { ...props, children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M12 2c1.5 0 3 1 3 1s1.2 1.8 1.2 4.2c0 2.4-1.2 5-3.6 7.4-2.4 2.4-5 3.6-7.4 3.6C3.8 18.2 2 17 2 17S3 15.5 3 14c0-2.1 1.5-3.6 1.5-3.6S7.5 9 9 9c2.4 0 3-7 3-7z",
        strokeWidth: "0",
        fill: "currentColor"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M14 10c.8.8 2 2 3 3",
        strokeWidth: "1.5",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  ] });
}
function SaveIcon(props) {
  return createStrokeIcon(
    "Save",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("path", { d: "M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" }),
      /* @__PURE__ */ jsx("polyline", { points: "17 21 17 13 7 13 7 21" }),
      /* @__PURE__ */ jsx("polyline", { points: "7 3 7 8 15 8" })
    ] }),
    props
  );
}
function SettingsIcon(props) {
  return createStrokeIcon(
    "Settings",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("path", { d: "M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.38a2 2 0 0 0-.73-2.73l-.15-.1a2 2 0 0 1-1-1.72v-.51a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" }),
      /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "3" })
    ] }),
    props
  );
}
function ShieldCheckIcon(props) {
  return createStrokeIcon(
    "ShieldCheck",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("path", { d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" }),
      /* @__PURE__ */ jsx("path", { d: "M9 12l2 2 4-4" })
    ] }),
    props
  );
}
function ShieldIcon(props) {
  return createStrokeIcon(
    "Shield",
    /* @__PURE__ */ jsx("path", { d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" }),
    props
  );
}
function TargetIcon(props) {
  return /* @__PURE__ */ jsxs(IconBase, { ...props, children: [
    /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10", strokeWidth: "1.2" }),
    /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "5", fill: "currentColor" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M22 12h-3M5 12H2M12 2v3M12 19v3",
        strokeWidth: "1.2",
        strokeLinecap: "round"
      }
    )
  ] });
}
function TerminalIcon(props) {
  return createStrokeIcon(
    "Terminal",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("polyline", { points: "4 17 10 11 4 5" }),
      /* @__PURE__ */ jsx("line", { x1: "12", y1: "19", x2: "20", y2: "19" })
    ] }),
    props
  );
}
function TrashIcon(props) {
  return createStrokeIcon(
    "Trash",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("polyline", { points: "3 6 5 6 21 6" }),
      /* @__PURE__ */ jsx("path", { d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" })
    ] }),
    props
  );
}
function TrendingUpIcon(props) {
  return createStrokeIcon(
    "TrendingUp",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("polyline", { points: "23 6 13.5 15.5 8.5 10.5 1 18" }),
      /* @__PURE__ */ jsx("polyline", { points: "17 6 23 6 23 12" })
    ] }),
    props
  );
}
function UploadIcon(props) {
  return createStrokeIcon(
    "Upload",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4" }),
      /* @__PURE__ */ jsx("polyline", { points: "17 8 12 3 7 8" }),
      /* @__PURE__ */ jsx("line", { x1: "12", y1: "3", x2: "12", y2: "15" })
    ] }),
    props
  );
}
function WalletIcon(props) {
  return createStrokeIcon(
    "Wallet",
    /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("path", { d: "M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-1" }),
      /* @__PURE__ */ jsx("path", { d: "M16 12h5" }),
      /* @__PURE__ */ jsx("circle", { cx: "16", cy: "12", r: "1" })
    ] }),
    props
  );
}
function ZapIcon(props) {
  return createStrokeIcon(
    "Zap",
    /* @__PURE__ */ jsx("polygon", { points: "13 2 3 14 12 14 11 22 21 10 12 10 13 2" }),
    props
  );
}
var Container = React2.forwardRef(
  ({ className, size = "lg", ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "mx-auto w-full px-4 sm:px-6 lg:px-8",
          {
            "max-w-screen-sm": size === "sm",
            "max-w-screen-md": size === "md",
            "max-w-screen-lg": size === "lg",
            "max-w-screen-xl": size === "xl",
            "max-w-full": size === "full"
          },
          className
        ),
        ...props
      }
    );
  }
);
Container.displayName = "Container";
var Grid = React2.forwardRef(
  ({ className, cols = 3, gap = "md", ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "grid",
          {
            "grid-cols-1": cols === 1,
            "grid-cols-1 sm:grid-cols-2": cols === 2,
            "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3": cols === 3,
            "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4": cols === 4,
            "grid-cols-1 sm:grid-cols-2 lg:grid-cols-5": cols === 5,
            "grid-cols-1 sm:grid-cols-2 lg:grid-cols-6": cols === 6,
            "grid-cols-1 sm:grid-cols-2 lg:grid-cols-12": cols === 12
          },
          {
            "gap-2": gap === "sm",
            "gap-4": gap === "md",
            "gap-6": gap === "lg",
            "gap-8": gap === "xl"
          },
          className
        ),
        ...props
      }
    );
  }
);
Grid.displayName = "Grid";
var Stack = React2.forwardRef(
  ({
    className,
    direction = "vertical",
    spacing = "md",
    align = "stretch",
    justify = "start",
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "flex",
          {
            "flex-col": direction === "vertical",
            "flex-row": direction === "horizontal"
          },
          {
            "gap-1": spacing === "xs",
            "gap-2": spacing === "sm",
            "gap-4": spacing === "md",
            "gap-6": spacing === "lg",
            "gap-8": spacing === "xl"
          },
          {
            "items-start": align === "start",
            "items-center": align === "center",
            "items-end": align === "end",
            "items-stretch": align === "stretch"
          },
          {
            "justify-start": justify === "start",
            "justify-center": justify === "center",
            "justify-end": justify === "end",
            "justify-between": justify === "between",
            "justify-around": justify === "around"
          },
          className
        ),
        ...props
      }
    );
  }
);
Stack.displayName = "Stack";
var Separator = React2.forwardRef(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      role: decorative ? "none" : "separator",
      "aria-orientation": orientation,
      "data-orientation": orientation,
      className: cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      ...props
    }
  )
);
Separator.displayName = "Separator";
var Checkbox = React2.forwardRef(
  ({ className, label, id, ...props }, ref) => {
    const checkboxId = id || React2.useId();
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: "checkbox",
          id: checkboxId,
          ref,
          role: "checkbox",
          "aria-checked": props.checked ? "true" : "false",
          className: cn(
            "peer h-4 w-4 rounded border-gray-300 text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className
          ),
          ...props
        }
      ),
      label && /* @__PURE__ */ jsx(
        "label",
        {
          htmlFor: checkboxId,
          className: "ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
          children: label
        }
      )
    ] });
  }
);
Checkbox.displayName = "Checkbox";
var RadioGroup = React2.forwardRef(
  ({
    className,
    options,
    value,
    onChange,
    name,
    orientation = "vertical",
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        ref,
        className: cn(
          "flex",
          orientation === "vertical" ? "flex-col gap-2" : "flex-row gap-4",
          className
        ),
        ...props,
        children: options.map((option) => {
          const id = `${name}-${option.value}`;
          return /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                type: "radio",
                id,
                name,
                value: option.value,
                checked: value === option.value,
                disabled: option.disabled,
                onChange: (e) => onChange?.(e.target.value),
                className: "h-4 w-4 border-gray-300 text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              }
            ),
            /* @__PURE__ */ jsx(
              "label",
              {
                htmlFor: id,
                className: "ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                children: option.label
              }
            )
          ] }, option.value);
        })
      }
    );
  }
);
RadioGroup.displayName = "RadioGroup";
var Switch = React2.forwardRef((props, ref) => {
  const {
    className,
    label,
    id,
    checked,
    onCheckedChange,
    onChange,
    disabled,
    ...restProps
  } = props;
  const switchId = id || React2.useId();
  const isChecked = checked ?? false;
  const { type: _type, ...buttonProps } = restProps;
  const handleChange = (e) => {
    onChange?.(e);
    onCheckedChange?.(e.target.checked);
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
    /* @__PURE__ */ jsxs(
      "label",
      {
        htmlFor: switchId,
        className: "relative inline-flex cursor-pointer items-center",
        children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "checkbox",
              id: switchId,
              ref,
              checked: isChecked,
              onChange: handleChange,
              className: "peer sr-only",
              disabled
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              role: "switch",
              "aria-checked": isChecked,
              disabled,
              className: cn(
                "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
                'bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[""] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring peer-focus:ring-offset-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-50',
                className
              ),
              onClick: () => {
                if (!disabled) {
                  const newChecked = !isChecked;
                  onCheckedChange?.(newChecked);
                }
              },
              ...buttonProps
            }
          )
        ]
      }
    ),
    label && /* @__PURE__ */ jsx("span", { className: "ml-3 text-sm font-medium text-foreground", children: label })
  ] });
});
Switch.displayName = "Switch";
var Textarea = React2.forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx(
      "textarea",
      {
        className: cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        ),
        ref,
        ...props
      }
    );
  }
);
Textarea.displayName = "Textarea";
var Select = React2.forwardRef(
  ({ className, options, placeholder, onChange, ...props }, ref) => {
    const handleChange = (e) => {
      onChange?.(e.target.value);
    };
    return /* @__PURE__ */ jsxs(
      "select",
      {
        ref,
        className: cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        ),
        onChange: handleChange,
        ...props,
        children: [
          placeholder && /* @__PURE__ */ jsx("option", { value: "", disabled: true, children: placeholder }),
          options.map((option) => /* @__PURE__ */ jsx(
            "option",
            {
              value: option.value,
              disabled: option.disabled,
              children: option.label
            },
            option.value
          ))
        ]
      }
    );
  }
);
Select.displayName = "Select";
function dedentCode(code) {
  const normalized = code.replace(/\t/g, "  ").replace(/[ \t]+$/gm, "");
  const lines = normalized.split("\n");
  if (lines.length <= 1) return normalized.trim();
  let start = 0;
  while (start < lines.length && lines[start].trim() === "") start++;
  let end = lines.length - 1;
  while (end >= 0 && lines[end].trim() === "") end--;
  if (start > end) return "";
  const relevantLines = lines.slice(start, end + 1);
  const nonEmpty = relevantLines.filter((l) => l.trim() !== "");
  const minIndent = nonEmpty.reduce((min, line) => {
    const m = line.match(/^\s*/)?.[0].length ?? 0;
    return Math.min(min, m);
  }, Infinity);
  return minIndent === Infinity || minIndent === 0 ? relevantLines.join("\n") : relevantLines.map(
    (l) => l.startsWith(" ".repeat(minIndent)) ? l.slice(minIndent) : l
  ).join("\n");
}
function CopyButton({ code }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2e3);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2e3);
    }
  }, [code]);
  return /* @__PURE__ */ jsx(
    "button",
    {
      onClick: handleCopy,
      className: "flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:bg-slate-700/50 hover:text-cyan-400 transition-all",
      children: /* @__PURE__ */ jsx(AnimatePresence, { mode: "wait", children: copied ? /* @__PURE__ */ jsx(
        motion.span,
        {
          initial: { opacity: 0, y: 5 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: -5 },
          className: "text-cyan-400",
          children: "Copied!"
        },
        "copied"
      ) : /* @__PURE__ */ jsx(
        motion.span,
        {
          initial: { opacity: 0, y: 5 },
          animate: { opacity: 1, y: 0 },
          exit: { opacity: 0, y: -5 },
          children: "Copy"
        },
        "copy"
      ) })
    }
  );
}
function CodeBlock({
  children,
  language = "typescript",
  showCopy = true,
  showHeader = true,
  className = "",
  variant = "glass"
}) {
  const codeString = useMemo(() => {
    if (typeof children === "string") return dedentCode(children);
    try {
      const raw = React2__default.Children.toArray(children).map(
        (c) => typeof c === "string" ? c : typeof c === "number" ? String(c) : ""
      ).join("");
      return dedentCode(raw);
    } catch {
      return "";
    }
  }, [children]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "group relative my-6 overflow-hidden rounded-2xl border transition-all",
        variant === "glass" ? "border-indigo-500/20 bg-slate-950/40 backdrop-blur-md shadow-2xl" : "border-slate-700 bg-slate-900 shadow-lg",
        className
      ),
      children: [
        showHeader && /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(
              "flex items-center justify-between px-4 py-2 border-b",
              variant === "glass" ? "border-indigo-500/10 bg-slate-900/20" : "border-slate-800 bg-slate-800/50"
            ),
            children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                /* @__PURE__ */ jsxs("div", { className: "flex gap-1.5", children: [
                  /* @__PURE__ */ jsx("div", { className: "h-2.5 w-2.5 rounded-full bg-red-500/20 border border-red-500/40" }),
                  /* @__PURE__ */ jsx("div", { className: "h-2.5 w-2.5 rounded-full bg-amber-500/20 border border-amber-500/40" }),
                  /* @__PURE__ */ jsx("div", { className: "h-2.5 w-2.5 rounded-full bg-emerald-500/20 border border-emerald-500/40" })
                ] }),
                /* @__PURE__ */ jsx("span", { className: "ml-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 font-mono", children: language })
              ] }),
              showCopy && /* @__PURE__ */ jsx(CopyButton, { code: codeString })
            ]
          }
        ),
        /* @__PURE__ */ jsx("pre", { className: "overflow-x-auto p-4 text-sm leading-relaxed scrollbar-thin scrollbar-thumb-slate-700", children: /* @__PURE__ */ jsx("code", { className: "font-mono block whitespace-pre text-slate-300", children: codeString }) })
      ]
    }
  );
}
function InlineCode({
  children,
  className = ""
}) {
  return /* @__PURE__ */ jsx(
    "code",
    {
      className: cn(
        "rounded-md bg-slate-800/50 border border-slate-700/50 px-1.5 py-0.5 text-xs font-mono text-cyan-400",
        className
      ),
      children
    }
  );
}
function Breadcrumb({ items, className }) {
  return /* @__PURE__ */ jsx("nav", { "aria-label": "Breadcrumb", className: cn("mb-8", className), children: /* @__PURE__ */ jsx("ol", { className: "flex items-center gap-2 text-sm", children: items.map((item, index) => /* @__PURE__ */ jsxs(
    motion.li,
    {
      initial: { opacity: 0, x: -10 },
      animate: { opacity: 1, x: 0 },
      transition: { delay: index * 0.1 },
      className: "flex items-center gap-2",
      children: [
        index > 0 && /* @__PURE__ */ jsx("span", { className: "text-slate-600", children: "/" }),
        index === items.length - 1 ? /* @__PURE__ */ jsx("span", { className: "text-cyan-400 font-medium", children: item.label }) : /* @__PURE__ */ jsx(
          "a",
          {
            href: item.href,
            className: "text-slate-400 hover:text-white transition-colors",
            children: item.label
          }
        )
      ]
    },
    item.href
  )) }) });
}
function NavItemComponent({
  href,
  label,
  icon: Icon,
  isActive,
  onClick,
  LinkComponent = "a"
}) {
  const Component = LinkComponent;
  return /* @__PURE__ */ jsx(Component, { href, onClick, className: "block group", children: /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-center gap-3 px-4 py-3 rounded-xl transition-all",
        isActive ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 glow-blue" : "text-slate-400 hover:text-white hover:bg-slate-800/50 border border-transparent hover:glow-blue"
      ),
      children: [
        /* @__PURE__ */ jsx(
          Icon,
          {
            className: cn(
              "w-5 h-5",
              isActive ? "text-cyan-400" : "text-slate-500 group-hover:text-slate-300"
            )
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold tracking-tight", children: label }),
        isActive && /* @__PURE__ */ jsx(
          motion.div,
          {
            layoutId: "sidebar-active",
            className: "ml-auto w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]"
          }
        )
      ]
    }
  ) });
}
function UserMenu({
  user,
  teams = [],
  currentTeamId,
  onSwitchTeam,
  onSignOut
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const currentWorkspaceName = currentTeamId === "personal" ? "Personal Workspace" : teams.find((t) => t.teamId === currentTeamId)?.team.name || "Team Workspace";
  return /* @__PURE__ */ jsxs("div", { className: "relative", ref: dropdownRef, children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: () => setIsOpen(!isOpen),
        className: "flex items-center gap-2 p-1.5 rounded-xl hover:bg-slate-800/50 transition-all border border-transparent hover:border-slate-700/50",
        children: [
          user.image ? /* @__PURE__ */ jsx(
            "img",
            {
              src: user.image,
              alt: user.name || "User",
              className: "w-8 h-8 rounded-lg border border-cyan-500/30"
            }
          ) : /* @__PURE__ */ jsx("div", { className: "w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-xs font-bold text-white", children: user.name?.[0] || user.email?.[0] }),
          /* @__PURE__ */ jsxs("div", { className: "text-left hidden sm:block", children: [
            /* @__PURE__ */ jsx("p", { className: "text-xs font-bold text-white leading-none mb-0.5", children: user.name || user.email?.split("@")[0] }),
            /* @__PURE__ */ jsx("p", { className: "text-[10px] text-slate-400 leading-none truncate max-w-[100px]", children: currentWorkspaceName })
          ] }),
          /* @__PURE__ */ jsx(
            "svg",
            {
              className: cn(
                "w-4 h-4 text-slate-500 transition-transform",
                isOpen && "rotate-180"
              ),
              fill: "none",
              viewBox: "0 0 24 24",
              stroke: "currentColor",
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  strokeLinecap: "round",
                  strokeLinejoin: "round",
                  strokeWidth: 2,
                  d: "M19 9l-7 7-7-7"
                }
              )
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: 10, scale: 0.95 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 10, scale: 0.95 },
        className: "absolute right-0 mt-2 w-64 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl z-50 overflow-hidden",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "p-4 border-b border-slate-800", children: [
            /* @__PURE__ */ jsx("p", { className: "text-sm font-bold text-white", children: user.name }),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-400 truncate", children: user.email })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "p-2 border-b border-slate-800", children: [
            /* @__PURE__ */ jsx("p", { className: "px-3 py-1 text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1", children: "Workspaces" }),
            /* @__PURE__ */ jsxs(
              "button",
              {
                onClick: () => {
                  onSwitchTeam?.("personal");
                  setIsOpen(false);
                },
                className: cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                  currentTeamId === "personal" ? "bg-cyan-500/10 text-cyan-400" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                ),
                children: [
                  /* @__PURE__ */ jsx("div", { className: "w-5 h-5 rounded bg-slate-700 flex items-center justify-center text-[10px] font-bold", children: "P" }),
                  "Personal"
                ]
              }
            ),
            teams.map((t) => /* @__PURE__ */ jsxs(
              "button",
              {
                onClick: () => {
                  onSwitchTeam?.(t.teamId);
                  setIsOpen(false);
                },
                className: cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                  currentTeamId === t.teamId ? "bg-purple-500/10 text-purple-400" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                ),
                children: [
                  /* @__PURE__ */ jsx("div", { className: "w-5 h-5 rounded bg-purple-600 flex items-center justify-center text-[10px] font-bold", children: t.team.name[0] }),
                  t.team.name
                ]
              },
              t.teamId
            ))
          ] }),
          /* @__PURE__ */ jsx("div", { className: "p-2", children: /* @__PURE__ */ jsxs(
            "button",
            {
              onClick: onSignOut,
              className: "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors",
              children: [
                /* @__PURE__ */ jsx(
                  "svg",
                  {
                    className: "w-4 h-4",
                    fill: "none",
                    viewBox: "0 0 24 24",
                    stroke: "currentColor",
                    children: /* @__PURE__ */ jsx(
                      "path",
                      {
                        strokeLinecap: "round",
                        strokeLinejoin: "round",
                        strokeWidth: 2,
                        d: "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      }
                    )
                  }
                ),
                "Sign out"
              ]
            }
          ) })
        ]
      }
    ) })
  ] });
}
function PlatformShell({
  children,
  user,
  teams = [],
  overallScore,
  activePage,
  pathname = "",
  onNavigate,
  onSignOut,
  onSwitchTeam,
  logoUrl = "/logo-text-transparent-dark-theme.png",
  navItems = [
    { href: "/dashboard", label: "Dashboard", icon: RocketIcon },
    { href: "/strategy", label: "Scan Strategy", icon: SettingsIcon },
    { href: "/trends", label: "Trends Explorer", icon: TrendingUpIcon },
    { href: "/map", label: "Codebase Map", icon: RobotIcon },
    { href: "/metrics", label: "Methodology", icon: ChartIcon }
  ],
  LinkComponent = "a"
}) {
  const [currentTeamId, setCurrentTeamId] = useState(
    "personal"
  );
  const handleSwitchTeam = (teamId) => {
    setCurrentTeamId(teamId);
    onSwitchTeam?.(teamId);
  };
  const Sidebar = () => /* @__PURE__ */ jsx("aside", { className: "hidden lg:flex flex-col w-64 bg-slate-950/50 border-r border-slate-800 h-screen sticky top-0 overflow-y-auto", children: /* @__PURE__ */ jsxs("div", { className: "p-6 flex flex-col h-full", children: [
    /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center mb-10", children: /* @__PURE__ */ jsx("img", { src: logoUrl, alt: "AIReady", className: "h-9 w-auto" }) }),
    /* @__PURE__ */ jsxs("nav", { className: "space-y-1.5 flex-1", children: [
      /* @__PURE__ */ jsx("p", { className: "px-4 py-2 text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2", children: "Workspace" }),
      navItems.map((item) => /* @__PURE__ */ jsx(
        NavItemComponent,
        {
          ...item,
          LinkComponent,
          isActive: pathname === item.href || item.href !== "/dashboard" && pathname.startsWith(item.href),
          onClick: (e) => {
            if (onNavigate) {
              e.preventDefault();
              onNavigate(item.href);
            }
          }
        },
        item.href
      ))
    ] }),
    /* @__PURE__ */ jsx("div", { className: "mt-auto pt-6 space-y-4", children: /* @__PURE__ */ jsxs("div", { className: "p-4 rounded-2xl bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs font-bold text-white mb-1", children: "AI Insights" }),
      /* @__PURE__ */ jsx("p", { className: "text-[10px] text-slate-400 leading-relaxed", children: overallScore != null ? `Your codebase is ${overallScore}% ready for AI agents.` : "Run a scan to see your AI-readiness score." })
    ] }) })
  ] }) });
  const Navbar = () => /* @__PURE__ */ jsx("header", { className: "sticky top-0 z-20 h-16 border-b border-indigo-500/10 backdrop-blur-md bg-slate-950/20 px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs("div", { className: "h-full flex items-center justify-between", children: [
    /* @__PURE__ */ jsx("div", { className: "flex items-center gap-4", children: /* @__PURE__ */ jsx("p", { className: "text-xs font-semibold text-slate-400 uppercase tracking-widest hidden sm:block glow-text", children: activePage || "Dashboard" }) }),
    /* @__PURE__ */ jsx("div", { className: "flex items-center gap-4", children: user && /* @__PURE__ */ jsx(
      UserMenu,
      {
        user,
        teams,
        currentTeamId,
        onSwitchTeam: handleSwitchTeam,
        onSignOut
      }
    ) })
  ] }) });
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "min-h-screen bg-[#0a0a0f]",
        user && "flex overflow-hidden"
      ),
      children: [
        user && /* @__PURE__ */ jsx(Sidebar, {}),
        /* @__PURE__ */ jsxs("div", { className: cn("flex-1 flex flex-col min-w-0", user && "h-screen"), children: [
          user && /* @__PURE__ */ jsx(Navbar, {}),
          /* @__PURE__ */ jsxs(
            "main",
            {
              className: cn("relative flex-1", user && "overflow-y-auto", "z-10"),
              children: [
                user && /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsxs("div", { className: "absolute inset-0 pointer-events-none -z-10 overflow-hidden", children: [
                    /* @__PURE__ */ jsx(
                      "div",
                      {
                        className: "absolute rounded-full blur-[60px] opacity-20 bg-radial-gradient from-blue-600/60 to-transparent w-96 h-96 -top-48 -right-48",
                        style: {
                          background: "radial-gradient(circle, rgba(59, 130, 246, 0.6), transparent)"
                        }
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "div",
                      {
                        className: "absolute rounded-full blur-[60px] opacity-20 bg-radial-gradient from-purple-600/60 to-transparent w-80 h-80 bottom-0 -left-40",
                        style: {
                          background: "radial-gradient(circle, rgba(139, 92, 246, 0.6), transparent)"
                        }
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "absolute inset-0 opacity-10 -z-10",
                      style: {
                        backgroundImage: "linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)",
                        backgroundSize: "50px 50px"
                      }
                    }
                  )
                ] }),
                children
              ]
            }
          )
        ] })
      ]
    }
  );
}
var ratingConfig = {
  excellent: {
    color: "bg-green-500",
    bgColor: "bg-green-100",
    label: "Excellent"
  },
  good: { color: "bg-emerald-500", bgColor: "bg-emerald-100", label: "Good" },
  fair: { color: "bg-amber-500", bgColor: "bg-amber-100", label: "Fair" },
  "needs-work": {
    color: "bg-orange-500",
    bgColor: "bg-orange-100",
    label: "Needs Work"
  },
  critical: { color: "bg-red-500", bgColor: "bg-red-100", label: "Critical" }
};
function getRating(score) {
  const coreRating = getRating$1(score);
  const ratingMap = {
    Excellent: "excellent",
    Good: "good",
    Fair: "fair",
    "Needs Work": "needs-work",
    Critical: "critical"
  };
  return ratingMap[coreRating] || "critical";
}
var sizeConfig = {
  sm: { height: "h-1.5", text: "text-xs", score: "text-sm" },
  md: { height: "h-2", text: "text-sm", score: "text-base" },
  lg: { height: "h-3", text: "text-base", score: "text-lg" }
};
function ScoreBar({
  score,
  maxScore = 100,
  label,
  showScore = true,
  size = "md",
  className
}) {
  const percentage = Math.min(100, Math.max(0, score / maxScore * 100));
  const rating = getRating(percentage);
  const config = ratingConfig[rating];
  const sizes = sizeConfig[size];
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-1", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("span", { className: cn("text-slate-700", sizes.text), children: label }),
      showScore && /* @__PURE__ */ jsxs("span", { className: cn("font-bold text-slate-900", sizes.score), children: [
        score,
        "/",
        maxScore
      ] })
    ] }),
    /* @__PURE__ */ jsx("div", { className: cn("w-full rounded-full bg-slate-200", sizes.height), children: /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "rounded-full transition-all duration-500",
          config.color,
          sizes.height
        ),
        style: { width: `${percentage}%` }
      }
    ) })
  ] });
}
function ScoreCard({
  score,
  title,
  breakdown,
  className
}) {
  const rating = getRating(score);
  const config = ratingConfig[rating];
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "rounded-xl border-2 border-slate-200 bg-white p-6 shadow-lg",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "mb-4", children: [
          /* @__PURE__ */ jsxs("div", { className: "text-4xl font-black text-slate-900", children: [
            score,
            "/100"
          ] }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: cn(
                "text-lg font-bold",
                `text-${rating === "excellent" ? "green" : rating === "good" ? "emerald" : rating === "fair" ? "amber" : rating === "needs-work" ? "orange" : "red"}-600`
              ),
              children: [
                config.label,
                " Rating"
              ]
            }
          ),
          title && /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-600 mt-1", children: title })
        ] }),
        breakdown && breakdown.length > 0 && /* @__PURE__ */ jsx("div", { className: "space-y-3", children: breakdown.map((item, index) => /* @__PURE__ */ jsx(
          ScoreBar,
          {
            score: item.score,
            label: item.label,
            size: "sm"
          },
          index
        )) }),
        breakdown && breakdown.length > 0 && /* @__PURE__ */ jsxs("div", { className: "mt-4 text-xs text-slate-600 bg-slate-50 p-3 rounded-lg", children: [
          /* @__PURE__ */ jsx("strong", { children: "Formula:" }),
          " ",
          breakdown.map((item) => `${item.score}\xD7${item.weight || 1}`).join(" + "),
          " ",
          "/ 100 = ",
          score
        ] })
      ]
    }
  );
}
function ScoreCircle({
  score,
  progress: customProgress,
  isInView = true,
  size = "md",
  showLabel = true,
  className
}) {
  const progress = customProgress ?? score;
  const getScoreColor = () => {
    if (score >= 80)
      return { stroke: "#10b981", glow: "rgba(16, 185, 129, 0.3)" };
    if (score >= 60)
      return { stroke: "#eab308", glow: "rgba(234, 179, 8, 0.3)" };
    return { stroke: "#ef4444", glow: "rgba(239, 68, 68, 0.3)" };
  };
  const scoreColor2 = getScoreColor();
  const sizeMap = {
    sm: "w-32 h-32",
    md: "w-48 h-48",
    lg: "w-64 h-64",
    xl: "w-80 h-80"
  };
  const textMap = {
    sm: { score: "text-3xl", label: "text-[8px]", title: "text-sm" },
    md: { score: "text-5xl", label: "text-[10px]", title: "text-lg" },
    lg: { score: "text-6xl", label: "text-xs", title: "text-xl" },
    xl: { score: "text-7xl", label: "text-sm", title: "text-2xl" }
  };
  const currentSize = sizeMap[size];
  const currentText = textMap[size];
  return /* @__PURE__ */ jsxs("div", { className: cn("relative", currentSize, className), children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        className: "absolute inset-0 rounded-full blur-2xl opacity-50 transition-all duration-500",
        style: { backgroundColor: scoreColor2.glow }
      }
    ),
    /* @__PURE__ */ jsx(
      "svg",
      {
        className: "w-full h-full transform -rotate-90 relative z-10",
        viewBox: "0 0 100 100",
        children: Array.from({ length: 20 }).map((_, i) => {
          const angle = i * 360 / 20;
          const startAngle = angle - 85;
          const radius = 42;
          const strokeWidth = 6;
          const gapAngle = 4;
          const startX = 50 + radius * Math.cos(startAngle * Math.PI / 180);
          const startY = 50 + radius * Math.sin(startAngle * Math.PI / 180);
          const endX = 50 + radius * Math.cos((startAngle + 360 / 20 - gapAngle) * Math.PI / 180);
          const endY = 50 + radius * Math.sin((startAngle + 360 / 20 - gapAngle) * Math.PI / 180);
          const isActive = i / 20 * 100 <= progress;
          return /* @__PURE__ */ jsx(
            motion.line,
            {
              x1: `${startX}`,
              y1: `${startY}`,
              x2: `${endX}`,
              y2: `${endY}`,
              stroke: isActive ? scoreColor2.stroke : "#334155",
              strokeWidth,
              strokeLinecap: "round",
              initial: { opacity: 0.3 },
              animate: {
                opacity: isActive ? 1 : 0.3,
                filter: isActive ? `drop-shadow(0 0 2px ${scoreColor2.stroke})` : "none"
              },
              transition: { duration: 0.3 }
            },
            i
          );
        })
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex flex-col items-center justify-center z-20", children: /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { scale: 0 },
        animate: isInView ? { scale: 1 } : { scale: 0 },
        transition: { duration: 0.5, delay: 0.3 },
        className: "text-center",
        children: [
          /* @__PURE__ */ jsx(
            motion.div,
            {
              className: cn("font-black mb-1", currentText.score),
              style: { color: scoreColor2.stroke },
              animate: {
                textShadow: `0 0 20px ${scoreColor2.glow}, 0 0 40px ${scoreColor2.glow}`
              },
              children: score
            }
          ),
          showLabel && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              "div",
              {
                className: cn(
                  "font-bold uppercase tracking-wider text-slate-400 mb-0.5",
                  currentText.label
                ),
                children: "AI Readiness"
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                className: cn("font-black tracking-tight", currentText.title),
                style: { color: scoreColor2.stroke },
                children: "SCORE"
              }
            )
          ] })
        ]
      }
    ) })
  ] });
}
var sizeClasses = {
  sm: "h-4 w-4",
  md: "h-8 w-8",
  lg: "h-12 w-12"
};
function LoadingSpinner({
  size = "md",
  className = ""
}) {
  return /* @__PURE__ */ jsx("div", { className: `flex items-center justify-center ${className}`, children: /* @__PURE__ */ jsx(
    "div",
    {
      className: `${sizeClasses[size]} animate-spin rounded-full border-2 border-slate-300 border-t-blue-600`
    }
  ) });
}
function LoadingOverlay({
  message = "Loading..."
}) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center min-h-[200px] gap-4", children: [
    /* @__PURE__ */ jsx(LoadingSpinner, { size: "lg" }),
    /* @__PURE__ */ jsx("p", { className: "text-sm text-slate-500 animate-pulse", children: message })
  ] });
}
function ErrorDisplay({
  title = "Something went wrong",
  message,
  retry,
  retryLabel = "Try again"
}) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center min-h-[200px] gap-4 p-8", children: [
    /* @__PURE__ */ jsx("div", { className: "rounded-full bg-red-100 p-3", children: /* @__PURE__ */ jsx(
      "svg",
      {
        className: "h-6 w-6 text-red-600",
        fill: "none",
        viewBox: "0 0 24 24",
        strokeWidth: "1.5",
        stroke: "currentColor",
        children: /* @__PURE__ */ jsx(
          "path",
          {
            strokeLinecap: "round",
            strokeLinejoin: "round",
            d: "M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
          }
        )
      }
    ) }),
    /* @__PURE__ */ jsxs("div", { className: "text-center", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-lg font-semibold text-slate-900", children: title }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500", children: message })
    ] }),
    retry && /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: retry,
        className: "mt-2 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors",
        children: [
          /* @__PURE__ */ jsx(
            "svg",
            {
              className: "h-4 w-4",
              fill: "none",
              viewBox: "0 0 24 24",
              strokeWidth: "1.5",
              stroke: "currentColor",
              children: /* @__PURE__ */ jsx(
                "path",
                {
                  strokeLinecap: "round",
                  strokeLinejoin: "round",
                  d: "M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
                }
              )
            }
          ),
          retryLabel
        ]
      }
    )
  ] });
}
function EmptyState({
  title,
  description,
  icon,
  action
}) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center justify-center min-h-[200px] gap-4 p-8", children: [
    icon && /* @__PURE__ */ jsx("div", { className: "rounded-full bg-slate-100 p-3", children: icon }),
    /* @__PURE__ */ jsxs("div", { className: "text-center", children: [
      /* @__PURE__ */ jsx("h3", { className: "text-lg font-semibold text-slate-900", children: title }),
      description && /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm text-slate-500", children: description })
    ] }),
    action && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: action.onClick,
        className: "mt-2 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors",
        children: action.label
      }
    )
  ] });
}
function FeedbackWidget({
  apiEndpoint = "/api/feedback",
  onSuccess,
  onError,
  title = "Share Feedback",
  description = "What features would you like to see? Found a bug? Let us know!",
  className
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setStatus("loading");
    try {
      const res = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message })
      });
      if (!res.ok) throw new Error("Failed to send feedback");
      onSuccess?.(message);
      setMessage("");
      setIsOpen(false);
    } catch (err) {
      onError?.(err);
    } finally {
      setStatus("idle");
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("fixed bottom-6 right-6 z-50", className), children: [
    /* @__PURE__ */ jsx(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, scale: 0.9, y: 20 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.9, y: 20 },
        className: "absolute bottom-full mb-4 right-0 w-80 rounded-2xl p-4 bg-slate-900 border border-cyan-500/30 shadow-2xl backdrop-blur-xl top-auto",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between mb-4", children: [
            /* @__PURE__ */ jsxs("h4", { className: "font-bold text-white flex items-center gap-2 text-sm", children: [
              /* @__PURE__ */ jsx(MessageSquare, { className: "w-4 h-4 text-cyan-400" }),
              title
            ] }),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: () => setIsOpen(false),
                className: "text-slate-500 hover:text-white",
                children: /* @__PURE__ */ jsx(X, { className: "w-4 h-4" })
              }
            )
          ] }),
          /* @__PURE__ */ jsx("p", { className: "text-xs text-slate-400 mb-4", children: description }),
          /* @__PURE__ */ jsxs("form", { onSubmit: handleSubmit, className: "space-y-3", children: [
            /* @__PURE__ */ jsx(
              "textarea",
              {
                autoFocus: true,
                value: message,
                onChange: (e) => setMessage(e.target.value),
                placeholder: "Type your feedback here...",
                required: true,
                className: "w-full bg-slate-800/50 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white h-24 resize-none focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-all"
              }
            ),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "submit",
                disabled: status === "loading" || !message.trim(),
                className: "w-full py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white font-bold rounded-lg text-sm transition-all flex items-center justify-center gap-2",
                children: status === "loading" ? /* @__PURE__ */ jsx(Loader2, { className: "w-4 h-4 animate-spin" }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                  /* @__PURE__ */ jsx(Send, { className: "w-3 h-3" }),
                  "Send Feedback"
                ] })
              }
            )
          ] })
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs(
      motion.button,
      {
        whileHover: { scale: 1.05 },
        whileTap: { scale: 0.95 },
        onClick: () => setIsOpen(!isOpen),
        className: "w-12 h-12 bg-gradient-to-br from-cyan-600 to-blue-600 text-white rounded-full flex items-center justify-center shadow-lg shadow-cyan-500/20 group overflow-hidden relative",
        children: [
          /* @__PURE__ */ jsx("div", { className: "absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" }),
          isOpen ? /* @__PURE__ */ jsx(X, { className: "w-6 h-6" }) : /* @__PURE__ */ jsx(MessageSquare, { className: "w-6 h-6" })
        ]
      }
    )
  ] });
}
var ThemeContext = createContext(void 0);
var STORAGE_KEY = "aiready-theme";
function getSystemTheme() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function getStoredTheme() {
  if (typeof window === "undefined") return "system";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light" || stored === "system") {
      return stored;
    }
  } catch {
  }
  return "system";
}
function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = STORAGE_KEY
}) {
  const [theme, setThemeState] = useState(defaultTheme);
  const [effectiveTheme, setEffectiveTheme] = useState("light");
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const storedTheme = getStoredTheme();
    setThemeState(storedTheme);
    setMounted(true);
  }, []);
  useEffect(() => {
    if (!mounted) return;
    const updateEffectiveTheme = () => {
      if (theme === "system") {
        setEffectiveTheme(getSystemTheme());
      } else {
        setEffectiveTheme(theme);
      }
    };
    updateEffectiveTheme();
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (theme === "system") {
        setEffectiveTheme(getSystemTheme());
      }
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, mounted]);
  useEffect(() => {
    if (!mounted) return;
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(effectiveTheme);
  }, [effectiveTheme, mounted]);
  const setTheme = (newTheme) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(storageKey, newTheme);
    } catch {
    }
  };
  if (!mounted) {
    return /* @__PURE__ */ jsx(
      ThemeContext.Provider,
      {
        value: {
          theme: defaultTheme,
          setTheme: () => {
          },
          effectiveTheme: "light"
        },
        children
      }
    );
  }
  return /* @__PURE__ */ jsx(ThemeContext.Provider, { value: { theme, setTheme, effectiveTheme }, children });
}
function useTheme() {
  const context = useContext(ThemeContext);
  if (context === void 0) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

// src/utils/colors.ts
var severityColors = {
  critical: "#ef4444",
  // red-500
  major: "#f59e0b",
  // amber-500
  minor: "#eab308",
  // yellow-500
  info: "#60a5fa"
  // blue-400
};
var domainColors = [
  "#3b82f6",
  // blue-500
  "#8b5cf6",
  // violet-500
  "#ec4899",
  // pink-500
  "#10b981",
  // green-500
  "#f59e0b",
  // amber-500
  "#06b6d4",
  // cyan-500
  "#f97316",
  // orange-500
  "#a855f7"
  // purple-500
];
var chartColors = {
  primary: "#3b82f6",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#06b6d4"
};
function getDomainColor(index) {
  return domainColors[index % domainColors.length];
}
function getSeverityColor(severity) {
  return severityColors[severity];
}
function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// src/utils/formatters.ts
function formatNumber(value) {
  return new Intl.NumberFormat("en-US").format(value);
}
function formatCompactNumber(value) {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1
  }).format(value);
}
function formatPercentage(value, decimals = 1) {
  return `${(value * 100).toFixed(decimals)}%`;
}
function formatFileSize(bytes) {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
}
function formatRelativeTime(date) {
  const now = /* @__PURE__ */ new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1e3);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);
  if (diffYear > 0) {
    return `${diffYear} year${diffYear > 1 ? "s" : ""} ago`;
  }
  if (diffMonth > 0) {
    return `${diffMonth} month${diffMonth > 1 ? "s" : ""} ago`;
  }
  if (diffDay > 0) {
    return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  }
  if (diffHour > 0) {
    return `${diffHour} hour${diffHour > 1 ? "s" : ""} ago`;
  }
  if (diffMin > 0) {
    return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
  }
  return "just now";
}
function formatDate(date) {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  }).format(date);
}
function formatDateTime(date) {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  }).format(date);
}
function formatDuration(ms) {
  const seconds = Math.floor(ms / 1e3);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
}
function formatMetric(value, unit = "number") {
  switch (unit) {
    case "bytes":
      return formatFileSize(value);
    case "percentage":
      return formatPercentage(value);
    case "duration":
      return formatDuration(value);
    default:
      return formatCompactNumber(value);
  }
}
function formatRange(min, max) {
  return `${formatCompactNumber(min)} - ${formatCompactNumber(max)}`;
}
function formatDecimal(value, decimals = 2) {
  return value.toFixed(decimals);
}
var SCORE_METADATA = {
  excellent: {
    color: "text-emerald-400",
    bg: "bg-emerald-900/30 border-emerald-500/30",
    label: "Excellent",
    glow: "shadow-emerald-500/20"
  },
  good: {
    color: "text-emerald-400",
    bg: "bg-emerald-900/30 border-emerald-500/30",
    label: "AI-Ready",
    glow: "shadow-emerald-500/20"
  },
  fair: {
    color: "text-amber-400",
    bg: "bg-amber-900/30 border-amber-500/30",
    label: "Fair",
    glow: "shadow-amber-500/20"
  },
  "needs-work": {
    color: "text-red-400",
    bg: "bg-red-900/30 border-red-500/30",
    label: "Needs Improvement",
    glow: "shadow-red-500/20"
  },
  critical: {
    color: "text-red-400",
    bg: "bg-red-900/30 border-red-500/30",
    label: "Critical Issues",
    glow: "shadow-red-500/20"
  }
};
var DEFAULT_METADATA = {
  color: "text-slate-400",
  bg: "bg-slate-800/50 border-slate-700",
  label: "Not analyzed",
  glow: ""
};
function getMetadata(score) {
  if (score == null) return DEFAULT_METADATA;
  const rating = getRatingSlug(score);
  return SCORE_METADATA[rating] || SCORE_METADATA.critical;
}
function scoreColor(score) {
  return getMetadata(score).color;
}
function scoreBg(score) {
  return getMetadata(score).bg;
}
function scoreLabel(score) {
  return getMetadata(score).label;
}
function scoreGlow(score) {
  return getMetadata(score).glow;
}
function getScoreRating(score) {
  if (score == null) return "critical";
  return getRatingSlug(score);
}
function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);
  return debouncedValue;
}
function useD3(renderFn, dependencies = []) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      const selection = d32.select(ref.current);
      renderFn(selection);
    }
  }, dependencies);
  return ref;
}
function useD3WithResize(renderFn, dependencies = []) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const selection = d32.select(ref.current);
    const render = () => renderFn(selection);
    render();
    const resizeObserver = new ResizeObserver(() => {
      render();
    });
    resizeObserver.observe(ref.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, dependencies);
  return ref;
}

// src/hooks/simulation-helpers.ts
function stabilizeNodes(nodes) {
  nodes.forEach((n) => {
    n.vx = 0;
    n.vy = 0;
    if (typeof n.x === "number") n.x = Number(n.x.toFixed(3));
    if (typeof n.y === "number") n.y = Number(n.y.toFixed(3));
  });
}
function seedCircularPositions(nodes, width, height) {
  const radius = Math.min(width, height) * 0.45;
  nodes.forEach((n, i) => {
    const angle = i * 2 * Math.PI / nodes.length;
    n.x = width / 2 + radius * Math.cos(angle);
    n.y = height / 2 + radius * Math.sin(angle);
    n.vx = (Math.random() - 0.5) * 2;
    n.vy = (Math.random() - 0.5) * 2;
  });
}
function seedRandomPositions(nodes, width, height) {
  nodes.forEach((n) => {
    n.x = Math.random() * width;
    n.y = Math.random() * height;
    n.vx = (Math.random() - 0.5) * 10;
    n.vy = (Math.random() - 0.5) * 10;
  });
}
function safelyStopSimulation(simulation, nodes, options = {}) {
  try {
    if (options.stabilize) {
      stabilizeNodes(nodes);
    }
    simulation.stop();
  } catch (error) {
    console.warn("AIReady: Failed to stop simulation safely:", error);
  }
}

// src/hooks/simulation-constants.ts
var SIMULATION_DEFAULTS = {
  CHARGE_STRENGTH: -300,
  LINK_DISTANCE: 100,
  LINK_STRENGTH: 1,
  COLLISION_STRENGTH: 1,
  COLLISION_RADIUS: 10,
  CENTER_STRENGTH: 0.1,
  ALPHA_DECAY: 0.0228,
  VELOCITY_DECAY: 0.4,
  ALPHA_TARGET: 0,
  WARM_ALPHA: 0.3,
  ALPHA_MIN: 0.01,
  TICK_THROTTLE_MS: 33,
  // ~30 fps
  MAX_SIMULATION_TIME_MS: 3e3,
  STABILIZE_ON_STOP: true
};
var FORCE_NAMES = {
  LINK: "link",
  CHARGE: "charge",
  CENTER: "center",
  COLLISION: "collision",
  X: "x",
  Y: "y"
};
var EVENT_NAMES = {
  TICK: "tick",
  END: "end"
};

// src/hooks/useForceSimulation.ts
function useForceSimulation(initialNodes, initialLinks, options) {
  const {
    chargeStrength = SIMULATION_DEFAULTS.CHARGE_STRENGTH,
    linkDistance = SIMULATION_DEFAULTS.LINK_DISTANCE,
    linkStrength = SIMULATION_DEFAULTS.LINK_STRENGTH,
    collisionStrength = SIMULATION_DEFAULTS.COLLISION_STRENGTH,
    collisionRadius = SIMULATION_DEFAULTS.COLLISION_RADIUS,
    centerStrength = SIMULATION_DEFAULTS.CENTER_STRENGTH,
    width,
    height,
    alphaDecay = SIMULATION_DEFAULTS.ALPHA_DECAY,
    velocityDecay = SIMULATION_DEFAULTS.VELOCITY_DECAY,
    alphaTarget = SIMULATION_DEFAULTS.ALPHA_TARGET,
    warmAlpha = SIMULATION_DEFAULTS.WARM_ALPHA,
    alphaMin = SIMULATION_DEFAULTS.ALPHA_MIN,
    onTick,
    stabilizeOnStop = SIMULATION_DEFAULTS.STABILIZE_ON_STOP,
    tickThrottleMs = SIMULATION_DEFAULTS.TICK_THROTTLE_MS,
    maxSimulationTimeMs = SIMULATION_DEFAULTS.MAX_SIMULATION_TIME_MS
  } = options;
  const [nodes, setNodes] = useState(initialNodes);
  const [links, setLinks] = useState(initialLinks);
  const [isRunning, setIsRunning] = useState(false);
  const [alpha, setAlpha] = useState(1);
  const simulationRef = useRef(null);
  const stopTimeoutRef = useRef(null);
  const forcesEnabledRef = useRef(true);
  const originalForcesRef = useRef({
    charge: chargeStrength,
    link: linkStrength
  });
  const nodesKey = initialNodes.map((n) => n.id).join("|");
  const linksKey = initialLinks.map((l) => {
    const sourceId = typeof l.source === "string" ? l.source : l.source?.id;
    const targetId = typeof l.target === "string" ? l.target : l.target?.id;
    const linkType = l.type || "";
    return `${sourceId}->${targetId}:${linkType}`;
  }).join("|");
  useEffect(() => {
    const nodesCopy = initialNodes.map((node) => ({ ...node }));
    const linksCopy = initialLinks.map((link) => ({ ...link }));
    try {
      seedCircularPositions(nodesCopy, width, height);
    } catch (error) {
      console.warn(
        "AIReady: Position seeding failed, using random fallback:",
        error
      );
      seedRandomPositions(nodesCopy, width, height);
    }
    const simulation = d32.forceSimulation(
      nodesCopy
    );
    applySimulationForces(simulation, linksCopy);
    configureSimulationParameters(simulation);
    simulationRef.current = simulation;
    const rafState = { rafId: null, lastUpdate: 0 };
    setupTickHandler(simulation, nodesCopy, linksCopy, rafState);
    setupStopTimer(simulation, nodesCopy, linksCopy);
    return () => cleanupSimulation(simulation, rafState);
  }, [
    nodesKey,
    linksKey,
    chargeStrength,
    linkDistance,
    linkStrength,
    collisionStrength,
    collisionRadius,
    centerStrength,
    width,
    height,
    alphaDecay,
    velocityDecay,
    alphaTarget,
    alphaMin,
    stabilizeOnStop,
    tickThrottleMs,
    maxSimulationTimeMs
  ]);
  const applySimulationForces = (simulation, linksCopy) => {
    try {
      const linkForce = d32.forceLink(linksCopy).id((d) => d.id).distance((d) => d.distance ?? linkDistance).strength(linkStrength);
      simulation.force(FORCE_NAMES.LINK, linkForce).force(FORCE_NAMES.CHARGE, d32.forceManyBody().strength(chargeStrength)).force(
        FORCE_NAMES.CENTER,
        d32.forceCenter(width / 2, height / 2).strength(centerStrength)
      ).force(
        FORCE_NAMES.COLLISION,
        d32.forceCollide().radius((d) => (d.size ?? 10) + collisionRadius).strength(collisionStrength)
      ).force(
        FORCE_NAMES.X,
        d32.forceX(width / 2).strength(Math.max(0.02, centerStrength * 0.5))
      ).force(
        FORCE_NAMES.Y,
        d32.forceY(height / 2).strength(Math.max(0.02, centerStrength * 0.5))
      );
    } catch (error) {
      console.warn("AIReady: Failed to configure simulation forces:", error);
    }
  };
  const configureSimulationParameters = (simulation) => {
    simulation.alphaDecay(alphaDecay).velocityDecay(velocityDecay).alphaMin(alphaMin).alphaTarget(alphaTarget).alpha(warmAlpha);
  };
  const setupStopTimer = (simulation, nodesCopy, linksCopy) => {
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
    }
    if (maxSimulationTimeMs > 0) {
      stopTimeoutRef.current = setTimeout(() => {
        safelyStopSimulation(simulation, nodesCopy, {
          stabilize: stabilizeOnStop
        });
        updateStateAfterStop(nodesCopy, linksCopy, 0);
      }, maxSimulationTimeMs);
    }
  };
  const updateStateAfterStop = (nodesCopy, linksCopy, currentAlpha) => {
    setIsRunning(false);
    setAlpha(currentAlpha);
    setNodes([...nodesCopy]);
    setLinks([...linksCopy]);
  };
  const setupTickHandler = (simulation, nodesCopy, linksCopy, rafState) => {
    const handleTick = () => {
      if (onTick) {
        try {
          onTick(nodesCopy, linksCopy, simulation);
        } catch (error) {
          console.warn("AIReady: Simulation onTick callback failed:", error);
        }
      }
      const currentAlpha = simulation.alpha();
      if (currentAlpha <= alphaMin) {
        safelyStopSimulation(simulation, nodesCopy, {
          stabilize: stabilizeOnStop
        });
        updateStateAfterStop(nodesCopy, linksCopy, currentAlpha);
        return;
      }
      syncStateOnTick(nodesCopy, linksCopy, currentAlpha, rafState);
    };
    simulation.on(EVENT_NAMES.TICK, handleTick);
    simulation.on(EVENT_NAMES.END, () => setIsRunning(false));
  };
  const syncStateOnTick = (nodesCopy, linksCopy, currentAlpha, rafState) => {
    const now = Date.now();
    if (rafState.rafId === null && now - rafState.lastUpdate >= tickThrottleMs) {
      rafState.rafId = requestAnimationFrame(() => {
        rafState.rafId = null;
        rafState.lastUpdate = Date.now();
        setNodes([...nodesCopy]);
        setLinks([...linksCopy]);
        setAlpha(currentAlpha);
        setIsRunning(currentAlpha > alphaMin);
      });
    }
  };
  const cleanupSimulation = (simulation, rafState) => {
    simulation.on(EVENT_NAMES.TICK, null);
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
    if (rafState.rafId !== null) cancelAnimationFrame(rafState.rafId);
    simulation.stop();
  };
  const restartSimulation = useCallback(() => {
    const sim = simulationRef.current;
    if (!sim) return;
    try {
      sim.alphaTarget(warmAlpha).restart();
      setIsRunning(true);
      if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
      if (maxSimulationTimeMs > 0) {
        stopTimeoutRef.current = setTimeout(() => {
          sim.alpha(0);
          sim.stop();
          setIsRunning(false);
        }, maxSimulationTimeMs);
      }
    } catch (error) {
      console.warn("AIReady: Failed to restart simulation:", error);
    }
  }, [warmAlpha, maxSimulationTimeMs]);
  const stopSimulation = useCallback(() => {
    if (simulationRef.current) {
      simulationRef.current.stop();
      setIsRunning(false);
    }
  }, []);
  const setForcesEnabled = useCallback(
    (enabled) => {
      const sim = simulationRef.current;
      if (!sim || forcesEnabledRef.current === enabled) return;
      forcesEnabledRef.current = enabled;
      try {
        const charge = sim.force(
          FORCE_NAMES.CHARGE
        );
        if (charge) {
          charge.strength(enabled ? originalForcesRef.current.charge : 0);
        }
        const link = sim.force(FORCE_NAMES.LINK);
        if (link) {
          link.strength(enabled ? originalForcesRef.current.link : 0);
        }
        sim.alpha(warmAlpha).restart();
      } catch (error) {
        console.warn("AIReady: Failed to toggle simulation forces:", error);
      }
    },
    [warmAlpha]
  );
  return {
    nodes,
    links,
    restart: restartSimulation,
    stop: stopSimulation,
    isRunning,
    alpha,
    setForcesEnabled
  };
}
function useDrag(simulation) {
  const handleDragStart = useCallback(
    (event, node) => {
      if (!simulation) return;
      if (!event.active) simulation.alphaTarget(0.3).restart();
      node.fx = node.x;
      node.fy = node.y;
    },
    [simulation]
  );
  const handleDragged = useCallback((event, node) => {
    node.fx = event.x;
    node.fy = event.y;
  }, []);
  const handleDragEnd = useCallback(
    (event, node) => {
      if (!simulation) return;
      if (!event.active) simulation.alphaTarget(0);
      node.fx = null;
      node.fy = null;
    },
    [simulation]
  );
  return {
    onDragStart: handleDragStart,
    onDrag: handleDragged,
    onDragEnd: handleDragEnd
  };
}
function pinNode(node) {
  node.fx = node.x;
  node.fy = node.y;
}
function unpinNode(node) {
  node.fx = null;
  node.fy = null;
}
function unpinAllNodes(nodes) {
  nodes.forEach(unpinNode);
}
function useGraphZoom(svgRef, gRef, enableZoom, setTransform, transformRef) {
  useEffect(() => {
    if (!enableZoom || !svgRef.current || !gRef.current) return;
    const svg = d32.select(svgRef.current);
    const g = d32.select(gRef.current);
    const zoom3 = d32.zoom().scaleExtent([0.1, 10]).on("zoom", (event) => {
      g.attr("transform", event.transform);
      transformRef.current = event.transform;
      setTransform(event.transform);
    });
    svg.call(zoom3);
    return () => {
      svg.on(".zoom", null);
    };
  }, [enableZoom, svgRef, gRef, setTransform, transformRef]);
}
function useWindowDrag(enableDrag, svgRef, transformRef, dragActiveRef, dragNodeRef, onDragEnd) {
  useEffect(() => {
    if (!enableDrag) return;
    const handleWindowMove = (event) => {
      if (!dragActiveRef.current || !dragNodeRef.current) return;
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const t = transformRef.current;
      const x = (event.clientX - rect.left - t.x) / t.k;
      const y = (event.clientY - rect.top - t.y) / t.k;
      dragNodeRef.current.fx = x;
      dragNodeRef.current.fy = y;
    };
    const handleWindowUp = () => {
      if (!dragActiveRef.current) return;
      onDragEnd();
      dragNodeRef.current = null;
      dragActiveRef.current = false;
    };
    const handleWindowLeave = (event) => {
      if (event.relatedTarget === null) handleWindowUp();
    };
    window.addEventListener("mousemove", handleWindowMove);
    window.addEventListener("mouseup", handleWindowUp);
    window.addEventListener("mouseout", handleWindowLeave);
    window.addEventListener("blur", handleWindowUp);
    return () => {
      window.removeEventListener("mousemove", handleWindowMove);
      window.removeEventListener("mouseup", handleWindowUp);
      window.removeEventListener("mouseout", handleWindowLeave);
      window.removeEventListener("blur", handleWindowUp);
    };
  }, [enableDrag, svgRef, transformRef, dragActiveRef, dragNodeRef, onDragEnd]);
}
function useNodeInteractions(enableDrag, _nodes, _pinnedNodes, setPinnedNodes, restart, stop) {
  const handleDragStart = useCallback(
    (event, node) => {
      if (!enableDrag) return;
      event.preventDefault();
      event.stopPropagation();
      pinNode(node);
      setPinnedNodes((prev) => /* @__PURE__ */ new Set([...prev, node.id]));
      stop();
    },
    [enableDrag, stop, setPinnedNodes]
  );
  const handleNodeDoubleClick = useCallback(
    (event, node) => {
      event.stopPropagation();
      if (!enableDrag) return;
      if (node.fx === null || node.fx === void 0) {
        pinNode(node);
        setPinnedNodes((prev) => /* @__PURE__ */ new Set([...prev, node.id]));
      } else {
        unpinNode(node);
        setPinnedNodes((prev) => {
          const next = new Set(prev);
          next.delete(node.id);
          return next;
        });
      }
      restart();
    },
    [enableDrag, restart, setPinnedNodes]
  );
  return { handleDragStart, handleNodeDoubleClick };
}

// src/charts/constants.ts
var DEFAULT_NODE_COLOR = "#64748b";
var DEFAULT_NODE_SIZE = 10;
var DEFAULT_LINK_COLOR = "#94a3b8";
var DEFAULT_LINK_WIDTH = 1;
var CIRCULAR_LAYOUT_RADIUS_RATIO = 0.35;
var FIT_VIEW_PADDING = 40;
var TRANSITION_DURATION_MS = 300;
var PACKAGE_BOUNDARY_FILL = "rgba(148,163,184,0.06)";
var PACKAGE_BOUNDARY_STROKE = "#475569";
var PACKAGE_BOUNDARY_STROKE_WIDTH = 2;
var PACKAGE_BOUNDARY_DASH = "6 6";
var PACKAGE_LABEL_FONT_SIZE = 11;
var PACKAGE_LABEL_COLOR = "#475569";

// src/charts/layout-utils.ts
function applyCircularLayout(nodes, width, height) {
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) * CIRCULAR_LAYOUT_RADIUS_RATIO;
  nodes.forEach((node, i) => {
    const angle = 2 * Math.PI * i / nodes.length;
    node.fx = centerX + Math.cos(angle) * radius;
    node.fy = centerY + Math.sin(angle) * radius;
    node.x = node.fx;
    node.y = node.fy;
  });
}
function applyHierarchicalLayout(nodes, width, height) {
  const groups = /* @__PURE__ */ new Map();
  nodes.forEach((n) => {
    const key = n.packageGroup || n.group || "root";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(n);
  });
  const groupArray = Array.from(groups.entries());
  const cols = Math.ceil(Math.sqrt(groupArray.length));
  const groupSpacingX = width * 0.8 / cols;
  const groupSpacingY = height * 0.8 / Math.ceil(groupArray.length / cols);
  groupArray.forEach(([groupKey, groupNodes], gi) => {
    const col = gi % cols;
    const row = Math.floor(gi / cols);
    const groupX = (col + 0.5) * groupSpacingX;
    const groupY = (row + 0.5) * groupSpacingY;
    if (groupKey.startsWith("pkg:") || groupKey === groupKey) {
      groupNodes.forEach((n, ni) => {
        const angle = 2 * Math.PI * ni / groupNodes.length;
        const r = Math.min(80, 20 + groupNodes.length * 8);
        n.fx = groupX + Math.cos(angle) * r;
        n.fy = groupY + Math.sin(angle) * r;
        n.x = n.fx;
        n.y = n.fy;
      });
    }
  });
}
function applyInitialForceLayout(nodes, width, height) {
  nodes.forEach((node) => {
    if (node.fx === void 0 || node.fx === null) {
      node.x = Math.random() * width;
      node.y = Math.random() * height;
    }
  });
}

// src/charts/force-directed/useGraphLayout.ts
function useGraphLayout(initialNodes, width, height, layout, restart) {
  const nodes = useMemo(() => {
    if (!initialNodes || !initialNodes.length) return initialNodes;
    const copy = initialNodes.map((n) => ({ ...n }));
    if (layout === "circular") applyCircularLayout(copy, width, height);
    else if (layout === "hierarchical")
      applyHierarchicalLayout(copy, width, height);
    else applyInitialForceLayout(copy, width, height);
    return copy;
  }, [initialNodes, width, height, layout]);
  useEffect(() => {
    if (!nodes || nodes.length === 0) return;
    if (layout === "circular") applyCircularLayout(nodes, width, height);
    else if (layout === "hierarchical")
      applyHierarchicalLayout(nodes, width, height);
    restart();
  }, [layout, nodes, width, height, restart]);
  return { nodes };
}
function useSimulationControls() {
  const restart = useCallback(() => {
  }, []);
  const stop = useCallback(() => {
  }, []);
  const setForcesEnabled = useCallback((enabled) => {
  }, []);
  return { restart, stop, setForcesEnabled };
}
function useImperativeHandleMethods({
  nodes,
  pinnedNodes,
  setPinnedNodes,
  restart,
  width,
  height,
  layout,
  handleLayoutChange,
  svgRef,
  gRef,
  setTransform,
  internalDragEnabledRef
}) {
  const pinAll = useCallback(() => {
    const newPinned = /* @__PURE__ */ new Set();
    nodes.forEach((node) => {
      pinNode(node);
      newPinned.add(node.id);
    });
    setPinnedNodes(newPinned);
    restart();
  }, [nodes, setPinnedNodes, restart]);
  const unpinAll = useCallback(() => {
    unpinAllNodes(nodes);
    setPinnedNodes(/* @__PURE__ */ new Set());
    restart();
  }, [nodes, setPinnedNodes, restart]);
  const resetLayout = useCallback(() => {
    unpinAllNodes(nodes);
    setPinnedNodes(/* @__PURE__ */ new Set());
    restart();
  }, [nodes, setPinnedNodes, restart]);
  const fitView = useCallback(() => {
    if (!svgRef.current || !nodes.length) return;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    nodes.forEach((node) => {
      if (node.x !== void 0 && node.y !== void 0) {
        const size = node.size || DEFAULT_NODE_SIZE;
        minX = Math.min(minX, node.x - size);
        maxX = Math.max(maxX, node.x + size);
        minY = Math.min(minY, node.y - size);
        maxY = Math.max(maxY, node.y + size);
      }
    });
    if (!isFinite(minX)) return;
    const scale = Math.min(
      (width - FIT_VIEW_PADDING * 2) / (maxX - minX),
      (height - FIT_VIEW_PADDING * 2) / (maxY - minY),
      10
    );
    const x = width / 2 - (minX + maxX) / 2 * scale;
    const y = height / 2 - (minY + maxY) / 2 * scale;
    if (gRef.current && svgRef.current) {
      const svg = d32.select(svgRef.current);
      const newTransform = d32.zoomIdentity.translate(x, y).scale(scale);
      svg.transition().duration(TRANSITION_DURATION_MS).call(d32.zoom().transform, newTransform);
      setTransform(newTransform);
    }
  }, [nodes, width, height, svgRef, gRef, setTransform]);
  const getPinnedNodes = useCallback(
    () => Array.from(pinnedNodes),
    [pinnedNodes]
  );
  const setDragMode = useCallback(
    (enabled) => {
      internalDragEnabledRef.current = enabled;
    },
    [internalDragEnabledRef]
  );
  const setLayoutMethod = useCallback(
    (newLayout) => {
      handleLayoutChange(newLayout);
    },
    [handleLayoutChange]
  );
  const getLayout = useCallback(() => layout, [layout]);
  return {
    pinAll,
    unpinAll,
    resetLayout,
    fitView,
    getPinnedNodes,
    setDragMode,
    setLayout: setLayoutMethod,
    getLayout
  };
}
var ControlButton = ({
  onClick,
  active = false,
  icon,
  label,
  disabled = false
}) => /* @__PURE__ */ jsxs("div", { className: "relative group", children: [
  /* @__PURE__ */ jsx(
    "button",
    {
      onClick,
      disabled,
      className: cn(
        "p-2 rounded-lg transition-all duration-200",
        active ? "bg-blue-500 text-white shadow-md hover:bg-blue-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200",
        disabled && "opacity-50 cursor-not-allowed hover:bg-gray-100",
        "dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:active:bg-blue-600"
      ),
      title: label,
      children: /* @__PURE__ */ jsx("span", { className: "text-lg", children: icon })
    }
  ),
  /* @__PURE__ */ jsx("div", { className: "absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50", children: label })
] });
var GraphControls = ({
  dragEnabled = true,
  onDragToggle,
  manualLayout = false,
  onManualLayoutToggle,
  onPinAll,
  onUnpinAll,
  onReset,
  onFitView,
  pinnedCount = 0,
  totalNodes = 0,
  visible = true,
  position = "top-left",
  className
}) => {
  if (!visible) return null;
  const positionClasses = {
    "top-left": "top-4 left-4",
    "top-right": "top-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "bottom-right": "bottom-4 right-4"
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "fixed z-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 border border-gray-200 dark:border-gray-700",
        positionClasses[position],
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
          /* @__PURE__ */ jsx(
            ControlButton,
            {
              onClick: () => onDragToggle?.(!dragEnabled),
              active: dragEnabled,
              icon: "\u270B",
              label: dragEnabled ? "Drag enabled" : "Drag disabled"
            }
          ),
          /* @__PURE__ */ jsx(
            ControlButton,
            {
              onClick: () => onManualLayoutToggle?.(!manualLayout),
              active: manualLayout,
              icon: "\u{1F527}",
              label: manualLayout ? "Manual layout: ON" : "Manual layout: OFF"
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "w-8 h-px bg-gray-300 dark:bg-gray-600 mx-auto my-1" }),
          /* @__PURE__ */ jsxs("div", { className: "flex gap-1", children: [
            /* @__PURE__ */ jsx(
              ControlButton,
              {
                onClick: () => onPinAll?.(),
                disabled: totalNodes === 0,
                icon: "\u{1F4CC}",
                label: `Pin all nodes (${totalNodes})`
              }
            ),
            /* @__PURE__ */ jsx(
              ControlButton,
              {
                onClick: () => onUnpinAll?.(),
                disabled: pinnedCount === 0,
                icon: "\u{1F4CD}",
                label: `Unpin all (${pinnedCount} pinned)`
              }
            )
          ] }),
          /* @__PURE__ */ jsx("div", { className: "w-8 h-px bg-gray-300 dark:bg-gray-600 mx-auto my-1" }),
          /* @__PURE__ */ jsx(
            ControlButton,
            {
              onClick: () => onFitView?.(),
              disabled: totalNodes === 0,
              icon: "\u{1F3AF}",
              label: "Fit all nodes in view"
            }
          ),
          /* @__PURE__ */ jsx(
            ControlButton,
            {
              onClick: () => onReset?.(),
              disabled: totalNodes === 0,
              icon: "\u21BA",
              label: "Reset to auto-layout"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400", children: [
          /* @__PURE__ */ jsxs("div", { className: "whitespace-nowrap", children: [
            /* @__PURE__ */ jsx("strong", { children: "Nodes:" }),
            " ",
            totalNodes
          ] }),
          pinnedCount > 0 && /* @__PURE__ */ jsxs("div", { className: "whitespace-nowrap", children: [
            /* @__PURE__ */ jsx("strong", { children: "Pinned:" }),
            " ",
            pinnedCount
          ] })
        ] })
      ]
    }
  );
};
GraphControls.displayName = "GraphControls";
var NodeItem = ({
  node,
  isSelected,
  isHovered,
  pinned,
  defaultNodeSize,
  defaultNodeColor,
  showLabel = true,
  onClick,
  onDoubleClick,
  onMouseEnter,
  onMouseLeave,
  onMouseDown
}) => {
  const nodeSize = node.size || defaultNodeSize;
  const nodeColor = node.color || defaultNodeColor;
  const x = node.x ?? 0;
  const y = node.y ?? 0;
  return /* @__PURE__ */ jsxs(
    "g",
    {
      className: "cursor-pointer node",
      "data-id": node.id,
      transform: `translate(${x},${y})`,
      onClick: () => onClick?.(node),
      onDoubleClick: (e) => onDoubleClick?.(e, node),
      onMouseEnter: () => onMouseEnter?.(node),
      onMouseLeave: () => onMouseLeave?.(),
      onMouseDown: (e) => onMouseDown?.(e, node),
      children: [
        /* @__PURE__ */ jsx(
          "circle",
          {
            r: nodeSize,
            fill: nodeColor,
            stroke: isSelected ? "#000" : isHovered ? "#666" : "none",
            strokeWidth: pinned ? 3 : isSelected ? 2.5 : isHovered ? 2 : 1.5,
            opacity: isHovered || isSelected ? 1 : 0.9
          }
        ),
        pinned && /* @__PURE__ */ jsx(
          "circle",
          {
            r: nodeSize + 4,
            fill: "none",
            stroke: "#ff6b6b",
            strokeWidth: 1,
            opacity: 0.5,
            className: "pointer-events-none"
          }
        ),
        showLabel && node.label && /* @__PURE__ */ jsx(
          "text",
          {
            y: nodeSize + 15,
            fill: "#333",
            fontSize: "12",
            textAnchor: "middle",
            dominantBaseline: "middle",
            pointerEvents: "none",
            className: "select-none",
            children: node.label
          }
        )
      ]
    },
    node.id
  );
};
var NodeItem_default = NodeItem;
var LinkItem = ({
  link,
  onClick,
  defaultWidth,
  showLabel = true,
  nodes = []
}) => {
  const src = link.source?.id ?? (typeof link.source === "string" ? link.source : void 0);
  const tgt = link.target?.id ?? (typeof link.target === "string" ? link.target : void 0);
  const getNodePosition = (nodeOrId) => {
    if (typeof nodeOrId === "object" && nodeOrId !== null) {
      const node = nodeOrId;
      return { x: node.x ?? 0, y: node.y ?? 0 };
    } else if (typeof nodeOrId === "string") {
      const found = nodes.find((n) => n.id === nodeOrId);
      if (found) return { x: found.x ?? 0, y: found.y ?? 0 };
    }
    return null;
  };
  const sourcePos = getNodePosition(link.source);
  const targetPos = getNodePosition(link.target);
  if (!sourcePos || !targetPos) {
    return null;
  }
  const midX = (sourcePos.x + targetPos.x) / 2;
  const midY = (sourcePos.y + targetPos.y) / 2;
  return /* @__PURE__ */ jsxs("g", { children: [
    /* @__PURE__ */ jsx(
      "line",
      {
        x1: sourcePos.x,
        y1: sourcePos.y,
        x2: targetPos.x,
        y2: targetPos.y,
        "data-source": src,
        "data-target": tgt,
        stroke: link.color,
        strokeWidth: link.width ?? defaultWidth ?? 1,
        opacity: 0.6,
        className: "cursor-pointer transition-opacity hover:opacity-100",
        onClick: () => onClick?.(link)
      }
    ),
    showLabel && link.label && /* @__PURE__ */ jsx(
      "text",
      {
        x: midX,
        y: midY,
        fill: "#666",
        fontSize: "10",
        textAnchor: "middle",
        dominantBaseline: "middle",
        pointerEvents: "none",
        children: link.label
      }
    )
  ] });
};
var LinkItem_default = LinkItem;
var PackageBoundaries = ({
  packageBounds
}) => {
  if (!packageBounds || Object.keys(packageBounds).length === 0) return null;
  return /* @__PURE__ */ jsx("g", { className: "package-boundaries", pointerEvents: "none", children: Object.entries(packageBounds).map(([pid, b]) => /* @__PURE__ */ jsxs("g", { children: [
    /* @__PURE__ */ jsx(
      "circle",
      {
        cx: b.x,
        cy: b.y,
        r: b.r,
        fill: PACKAGE_BOUNDARY_FILL,
        stroke: PACKAGE_BOUNDARY_STROKE,
        strokeWidth: PACKAGE_BOUNDARY_STROKE_WIDTH,
        strokeDasharray: PACKAGE_BOUNDARY_DASH,
        opacity: 0.9
      }
    ),
    /* @__PURE__ */ jsx(
      "text",
      {
        x: b.x,
        y: Math.max(12, b.y - b.r + 14),
        fill: PACKAGE_LABEL_COLOR,
        fontSize: PACKAGE_LABEL_FONT_SIZE,
        textAnchor: "middle",
        pointerEvents: "none",
        children: pid.replace(/^pkg:/, "")
      }
    )
  ] }, pid)) });
};
PackageBoundaries.displayName = "PackageBoundaries";
var GraphCanvas = ({
  svgRef,
  gRef,
  width,
  height,
  className,
  nodes,
  links,
  pinnedNodes,
  selectedNodeId,
  hoveredNodeId,
  defaultNodeColor = DEFAULT_NODE_COLOR,
  defaultNodeSize = DEFAULT_NODE_SIZE,
  defaultLinkColor = DEFAULT_LINK_COLOR,
  defaultLinkWidth = DEFAULT_LINK_WIDTH,
  showNodeLabels = true,
  showLinkLabels = false,
  onNodeClick,
  onNodeHover,
  onLinkClick,
  packageBounds,
  handleNodeDoubleClick,
  handleDragStart,
  restart,
  setPinnedNodes
}) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      ref: svgRef,
      width,
      height,
      className: cn("bg-white dark:bg-gray-900", className),
      onDoubleClick: () => {
        unpinAllNodes(nodes);
        setPinnedNodes(/* @__PURE__ */ new Set());
        restart();
      },
      children: [
        /* @__PURE__ */ jsx("defs", { children: /* @__PURE__ */ jsx(
          "marker",
          {
            id: "arrow",
            viewBox: "0 0 10 10",
            refX: "20",
            refY: "5",
            markerWidth: "6",
            markerHeight: "6",
            orient: "auto",
            children: /* @__PURE__ */ jsx("path", { d: "M 0 0 L 10 5 L 0 10 z", fill: defaultLinkColor })
          }
        ) }),
        /* @__PURE__ */ jsxs("g", { ref: gRef, children: [
          links.map((link, i) => /* @__PURE__ */ jsx(
            LinkItem_default,
            {
              link,
              onClick: onLinkClick,
              defaultWidth: defaultLinkWidth,
              showLabel: showLinkLabels,
              nodes
            },
            `link-${i}`
          )),
          nodes.map((node) => /* @__PURE__ */ jsx(
            NodeItem_default,
            {
              node,
              isSelected: selectedNodeId === node.id,
              isHovered: hoveredNodeId === node.id,
              pinned: pinnedNodes.has(node.id),
              defaultNodeSize,
              defaultNodeColor,
              showLabel: showNodeLabels,
              onClick: onNodeClick,
              onDoubleClick: handleNodeDoubleClick,
              onMouseEnter: (n) => onNodeHover?.(n),
              onMouseLeave: () => onNodeHover?.(null),
              onMouseDown: handleDragStart
            },
            node.id
          )),
          /* @__PURE__ */ jsx(PackageBoundaries, { packageBounds: packageBounds || {} })
        ] })
      ]
    }
  );
};
var ForceDirectedGraph = forwardRef(
  ({
    nodes: initialNodes,
    links: initialLinks,
    width,
    height,
    enableZoom = true,
    enableDrag = true,
    onNodeClick,
    onNodeHover,
    onLinkClick,
    selectedNodeId,
    hoveredNodeId,
    defaultNodeColor,
    defaultNodeSize,
    defaultLinkColor,
    defaultLinkWidth,
    showNodeLabels,
    showLinkLabels,
    className,
    manualLayout = false,
    onManualLayoutChange,
    packageBounds,
    layout: externalLayout,
    onLayoutChange
  }, ref) => {
    const svgRef = useRef(null);
    const gRef = useRef(null);
    const [transform, setTransform] = useState({ k: 1, x: 0, y: 0 });
    const transformRef = useRef(transform);
    const dragNodeRef = useRef(null);
    const dragActiveRef = useRef(false);
    const [pinnedNodes, setPinnedNodes] = useState(/* @__PURE__ */ new Set());
    const internalDragEnabledRef = useRef(enableDrag);
    const [layout, setLayout] = useState(externalLayout || "force");
    useEffect(() => {
      if (externalLayout && externalLayout !== layout)
        setLayout(externalLayout);
    }, [externalLayout, layout]);
    const handleLayoutChange = useCallback(
      (newLayout) => {
        setLayout(newLayout);
        onLayoutChange?.(newLayout);
      },
      [onLayoutChange]
    );
    useEffect(() => {
      internalDragEnabledRef.current = enableDrag;
    }, [enableDrag]);
    const { restart, stop, setForcesEnabled } = useSimulationControls();
    const { nodes } = useGraphLayout(
      initialNodes,
      width,
      height,
      layout,
      restart
    );
    useEffect(() => {
      setForcesEnabled(!(manualLayout || pinnedNodes.size > 0));
    }, [manualLayout, pinnedNodes, setForcesEnabled]);
    useImperativeHandle(
      ref,
      () => useImperativeHandleMethods({
        nodes,
        pinnedNodes,
        setPinnedNodes,
        restart,
        width,
        height,
        layout,
        handleLayoutChange,
        svgRef,
        gRef,
        setTransform,
        internalDragEnabledRef
      }),
      [
        nodes,
        pinnedNodes,
        restart,
        width,
        height,
        layout,
        handleLayoutChange,
        setForcesEnabled
      ]
    );
    useEffect(() => {
      onManualLayoutChange?.(manualLayout);
    }, [manualLayout, onManualLayoutChange]);
    useGraphZoom(svgRef, gRef, enableZoom, setTransform, transformRef);
    useWindowDrag(
      enableDrag,
      svgRef,
      transformRef,
      dragActiveRef,
      dragNodeRef,
      () => {
        setForcesEnabled(true);
        restart();
      }
    );
    useEffect(() => {
      if (!gRef.current) return;
      const g = d32.select(gRef.current);
      g.selectAll("g.node").each(function() {
        const d = d32.select(this).datum();
        if (d)
          d32.select(this).attr(
            "transform",
            `translate(${d.x || 0},${d.y || 0})`
          );
      });
      g.selectAll("line").each(function() {
        const l = d32.select(this).datum();
        if (!l) return;
        const s = typeof l.source === "object" ? l.source : nodes.find((n) => n.id === l.source);
        const t = typeof l.target === "object" ? l.target : nodes.find((n) => n.id === l.target);
        if (s && t)
          d32.select(this).attr("x1", s.x).attr("y1", s.y).attr("x2", t.x).attr("y2", t.y);
      });
    }, [nodes, initialLinks]);
    const { handleDragStart, handleNodeDoubleClick } = useNodeInteractions(
      enableDrag,
      nodes,
      pinnedNodes,
      setPinnedNodes,
      restart,
      stop
    );
    useEffect(() => {
      if (!gRef.current || !enableDrag) return;
      const g = d32.select(gRef.current);
      const dragBehavior = d32.drag().on("start", (event) => {
        const target = event.sourceEvent?.target || event.target;
        const id = target.closest?.("g.node")?.getAttribute("data-id");
        if (!id || !internalDragEnabledRef.current) return;
        const node = nodes.find((n) => n.id === id);
        if (!node) return;
        if (!event.active) restart();
        dragActiveRef.current = true;
        dragNodeRef.current = node;
      }).on("drag", (event) => {
        if (!dragActiveRef.current || !dragNodeRef.current || !svgRef.current)
          return;
        const rect = svgRef.current.getBoundingClientRect();
        dragNodeRef.current.fx = (event.sourceEvent.clientX - rect.left - transform.x) / transform.k;
        dragNodeRef.current.fy = (event.sourceEvent.clientY - rect.top - transform.y) / transform.k;
      }).on("end", () => {
        setForcesEnabled(true);
        restart();
      });
      g.selectAll("g.node").call(dragBehavior);
      return () => {
        g.selectAll("g.node").on(".drag", null);
      };
    }, [gRef, enableDrag, nodes, transform, restart, setForcesEnabled]);
    return /* @__PURE__ */ jsx(
      GraphCanvas,
      {
        svgRef,
        gRef,
        width,
        height,
        className,
        nodes,
        links: initialLinks,
        pinnedNodes,
        selectedNodeId,
        hoveredNodeId,
        defaultNodeColor,
        defaultNodeSize,
        defaultLinkColor,
        defaultLinkWidth,
        showNodeLabels,
        showLinkLabels,
        onNodeClick,
        onNodeHover,
        onLinkClick,
        packageBounds,
        handleNodeDoubleClick,
        handleDragStart,
        restart,
        setPinnedNodes
      }
    );
  }
);
ForceDirectedGraph.displayName = "ForceDirectedGraph";

export { AlertCircleIcon, AlertTriangleIcon, ArrowRightIcon, Badge, BrainIcon, Breadcrumb, Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, ChartIcon, Checkbox, ClockIcon, CodeBlock, Container, EmptyState, ErrorDisplay, FeedbackWidget, FileIcon, ForceDirectedGraph, GlassCard, GlassCardContent, GlassCardHeader, GraphControls, Grid, HammerIcon, InfoIcon, InlineCode, Input, Label, LoadingOverlay, LoadingSpinner, Modal, PlatformShell, PlayIcon, RadioGroup, RefreshCwIcon, RobotIcon, RocketIcon, SaveIcon, ScoreBar, ScoreCard, ScoreCircle, Select, Separator, SettingsIcon, ShieldCheckIcon, ShieldIcon, Stack, Switch, TargetIcon, TerminalIcon, Textarea, ThemeProvider, TrashIcon, TrendingUpIcon, UploadIcon, WalletIcon, ZapIcon, badgeVariants, buttonVariants, chartColors, cn, domainColors, formatCompactNumber, formatDate, formatDateTime, formatDecimal, formatDuration, formatFileSize, formatMetric, formatNumber, formatPercentage, formatRange, formatRelativeTime, getDomainColor, getScoreRating, getSeverityColor, hexToRgba, scoreBg, scoreColor, scoreGlow, scoreLabel, severityColors, useD3, useD3WithResize, useDebounce, useDrag, useForceSimulation, useTheme };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map